import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import { ThemeLoader } from "../../shared/components/ThemeLoader";
import { OrchyLogo } from "../ui/OrchyLogo";
import { SEO } from "../SEO";
import { sendFirebasePasswordReset } from "../../lib/firebase";

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail]           = useState("");
  const [error, setError]           = useState("");
  const [sent, setSent]             = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) { setError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email."); return; }

    setIsSubmitting(true);
    try {
      await sendFirebasePasswordReset(email.trim().toLowerCase());
      setSent(true);
    } catch (err: any) {
      // Firebase returns "auth/user-not-found" for unknown emails — surface a safe message
      const code: string = err?.code ?? "";
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        setError("No account found for that email address.");
      } else {
        setError(err.message || "Failed to send reset email. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <SEO title="Reset Password" description="Reset your Orchy account password." noIndex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <OrchyLogo size={28} />
            <h1 className="text-3xl font-bold text-text">Orchy</h1>
          </div>
          <p className="text-secondary">Workflow Orchestration Platform</p>
        </div>

        <div className="bg-surface rounded-lg border border-edge shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate("/login")} className="text-secondary hover:text-text transition" type="button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-text">Reset Password</h2>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {sent ? (
            <div className="space-y-6">
              <div className="p-4 bg-brand/8 border border-brand/30 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-text">Check your inbox</p>
                  <p className="text-xs text-secondary mt-0.5">
                    A reset link was sent to <span className="font-mono">{email}</span>. Follow it to set a new password.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="w-full text-sm text-secondary hover:text-text transition text-center"
              >
                Send to a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-2">
                  Email <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-secondary" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }}
                    placeholder="you@example.com"
                    disabled={isSubmitting}
                    autoFocus
                    className="w-full bg-base border border-edge rounded-lg pl-10 pr-4 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:ring-brand focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand hover:bg-brand-dim text-on-primary font-semibold py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && <ThemeLoader size={16} />}
                {isSubmitting ? "Sending…" : "Send Reset Email"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-secondary">
              Remember your password?{" "}
              <Link to="/login" className="text-brand hover:text-brand-light transition font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};